import { useTranslation } from 'react-i18next'

import type { ShipmentDetail } from '#/shared/api/generated/shipment/models'
import { cn } from '#/shared/lib/utils'
import { CallableEmail } from '#/shared/ui/email-address'
import { BuildingIcon, PencilIcon, UserIcon } from '#/shared/ui/icons'
import { CallablePhone } from '#/shared/ui/phone-number'
import { Skeleton } from '#/shared/ui/skeleton'

interface CustomerCardProps {
  shipment?: ShipmentDetail
  isLoading?: boolean
  showCompany?: boolean
  onEdit?: () => void
}

interface ContactLine {
  value: string
  caption?: string
  callable?: boolean
}

function joinName(first?: string | null, last?: string | null): string {
  const name = [first, last].filter(Boolean).join(' ').trim()
  return name.length > 0 ? name : ''
}

function pickString(value: string | null | undefined): string | undefined {
  return value && value.length > 0 ? value : undefined
}

function lineFor(
  value: string | undefined,
  caption?: string,
  callable?: boolean,
): ContactLine | undefined {
  if (!value) return undefined
  return { value, caption, callable }
}

interface CustomerColumnsData {
  company: string
  companyLines: ContactLine[]
  contactName: string
  contactLines: ContactLine[]
}

function compactLines(lines: (ContactLine | undefined)[]): ContactLine[] {
  return lines.filter((line): line is ContactLine => Boolean(line))
}

function buildCompanyLines(
  customer: ShipmentDetail['customer'] | undefined,
): ContactLine[] {
  return compactLines([
    lineFor(pickString(customer?.billing_email)),
    lineFor(pickString(customer?.company_phone), undefined, true),
  ])
}

function buildContactLines(
  customer: ShipmentDetail['customer'] | undefined,
  t: (key: string) => string,
): ContactLine[] {
  return compactLines([
    lineFor(
      pickString(customer?.phone),
      t('orderDetails.customer.phoneMain'),
      true,
    ),
    lineFor(
      pickString(customer?.secondary_phone),
      t('orderDetails.customer.phoneAlt'),
      true,
    ),
    lineFor(pickString(customer?.email)),
    lineFor(pickString(customer?.secondary_email)),
  ])
}

function useCustomerColumns(
  customer: ShipmentDetail['customer'] | undefined,
): CustomerColumnsData {
  const { t } = useTranslation()
  return {
    company: pickString(customer?.company) ?? '',
    companyLines: buildCompanyLines(customer),
    contactName: joinName(customer?.first_name, customer?.last_name),
    contactLines: buildContactLines(customer, t),
  }
}

export function CustomerCard({
  shipment,
  isLoading = false,
  showCompany = false,
  onEdit,
}: CustomerCardProps) {
  const { t } = useTranslation()
  const { company, companyLines, contactName, contactLines } =
    useCustomerColumns(shipment?.customer ?? undefined)
  const shipmentId = shipment?.id
  const customerId = shipment?.customer?.id ?? undefined

  return (
    <div className="flex w-full flex-col gap-2.5 rounded-md border border-gray-200 bg-white pt-2.5 pb-5">
      <div className="flex flex-col gap-1.25">
        <div className="flex h-8 items-center justify-between px-5 pb-1.25">
          <h3 className="text-base font-semibold text-gray-900">
            {t('orderDetails.customer.title')}
          </h3>
          <button
            type="button"
            aria-label={t('orderDetails.customer.edit')}
            onClick={onEdit}
            className="flex size-8 cursor-pointer items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
          >
            <PencilIcon className="size-3.5" />
          </button>
        </div>
        <div className="h-px w-full bg-gray-200" />
      </div>

      <div className="flex w-full items-start gap-2.5 px-2.5">
        {isLoading ? (
          <CustomerColumnsSkeleton showCompany={showCompany} />
        ) : (
          <CustomerColumns
            showCompany={showCompany}
            company={company}
            companyLines={companyLines}
            contactName={contactName}
            contactLines={contactLines}
            shipmentId={shipmentId}
            customerId={customerId}
          />
        )}
      </div>
    </div>
  )
}

function CustomerColumns({
  showCompany,
  company,
  companyLines,
  contactName,
  contactLines,
  shipmentId,
  customerId,
}: {
  showCompany: boolean
  company: string
  companyLines: ContactLine[]
  contactName: string
  contactLines: ContactLine[]
  shipmentId?: string
  customerId?: string
}) {
  return (
    <>
      {showCompany && (
        <CustomerColumn
          icon={<BuildingIcon className="size-5 shrink-0 text-gray-500" />}
          primary={company}
          lines={companyLines}
          shipmentId={shipmentId}
          customerId={customerId}
        />
      )}
      <CustomerColumn
        icon={<UserIcon className="size-5 shrink-0 text-gray-500" />}
        primary={contactName}
        lines={contactLines}
        noLeftPad={showCompany}
        shipmentId={shipmentId}
        customerId={customerId}
      />
    </>
  )
}

function CustomerColumnsSkeleton({ showCompany }: { showCompany: boolean }) {
  return (
    <>
      {showCompany && (
        <CustomerColumnSkeleton
          icon={<BuildingIcon className="size-5 shrink-0 text-gray-500" />}
        />
      )}
      <CustomerColumnSkeleton
        icon={<UserIcon className="size-5 shrink-0 text-gray-500" />}
        noLeftPad={showCompany}
      />
    </>
  )
}

function CustomerColumn({
  icon,
  primary,
  lines,
  noLeftPad,
  shipmentId,
  customerId,
}: {
  icon: React.ReactNode
  primary: string
  lines: ContactLine[]
  noLeftPad?: boolean
  shipmentId?: string
  customerId?: string
}) {
  if (!primary && lines.length === 0) return null
  return (
    <div
      className={cn(
        'flex min-w-0 flex-1 items-start gap-2.5 py-0.75',
        noLeftPad ? 'pr-2.5' : 'px-2.5',
      )}
    >
      {icon}
      <div className="flex min-w-0 flex-col gap-1">
        {primary && (
          <span className="truncate text-sm font-medium text-gray-900">
            {primary}
          </span>
        )}
        {lines.map((line, index) => (
          <ContactLineRow
            key={index}
            line={line}
            name={primary}
            shipmentId={shipmentId}
            customerId={customerId}
          />
        ))}
      </div>
    </div>
  )
}

function ContactLineRow({
  line,
  name,
  shipmentId,
  customerId,
}: {
  line: ContactLine
  name: string
  shipmentId?: string
  customerId?: string
}) {
  return (
    <span className="flex items-center gap-1.75 text-sm text-gray-500">
      {line.callable ? (
        <CallablePhone
          value={line.value}
          smsTarget={{ phone: line.value, name, shipmentId, customerId }}
          hideIcon
        />
      ) : (
        <CallableEmail
          value={line.value}
          target={{ email: line.value, name, shipmentId, customerId }}
          hideIcon
        />
      )}
      {line.caption && (
        <>
          <span
            aria-hidden
            className="size-1 shrink-0 rounded-full bg-gray-300"
          />
          <span className="shrink-0 text-xs">{line.caption}</span>
        </>
      )}
    </span>
  )
}

function CustomerColumnSkeleton({
  icon,
  noLeftPad,
}: {
  icon: React.ReactNode
  noLeftPad?: boolean
}) {
  return (
    <div
      className={cn(
        'flex min-w-0 flex-1 items-start gap-2.5 py-0.75',
        noLeftPad ? 'pr-2.5' : 'px-2.5',
      )}
    >
      {icon}
      <div className="flex min-w-0 flex-col gap-1">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-4 w-28" />
      </div>
    </div>
  )
}
